import { Component, DoCheck, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CampaignService } from '../../../services/campaign/campaign.service';
import { Campaign } from './campaign';
import { CampaignStep } from './campaign-step';
import { CampaignHeaderComponent } from './header/campaign-header.component';
import { CampaignStepsComponent } from './steps/campaign-steps.component';
import { CampaignStatusComponent } from './status/campaign-status.component';
import { CampaignStep1Component } from './step-1/campaign-step-1.component';
import { CampaignStep2Component } from './step-2/campaign-step-2.component';
import { CampaignStep3Component } from './step-3/campaign-step-3.component';
import { CampaignStep4Component } from './step-4/campaign-step-4.component';
import { CampaignStep5Component } from './step-5/campaign-step-5.component';
import * as moment from 'moment';
import { MessageService } from 'primeng/api';
import {ConfirmationService} from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';


@Component({
  selector: 'new-campaign',
  templateUrl: 'new-campaign.html',
  styleUrls: ['./new-campaign.css'],
  providers: [CampaignService]
})
export class NewCampaignComponent implements DoCheck, OnDestroy, OnInit {

  @ViewChild(CampaignHeaderComponent) campaignHeader: CampaignHeaderComponent;
  @ViewChild(CampaignStepsComponent) campaignSteps: CampaignStepsComponent;
  @ViewChild(CampaignStatusComponent) campaignStatus: CampaignStatusComponent;
  @ViewChild(CampaignStep1Component) step1: CampaignStep1Component;
  @ViewChild(CampaignStep2Component) step2: CampaignStep2Component;
  @ViewChild(CampaignStep3Component) step3: CampaignStep3Component;
  @ViewChild(CampaignStep4Component) step4: CampaignStep4Component;
  @ViewChild(CampaignStep5Component) step5: CampaignStep5Component;

  private urlParams: any;
  public campaign: Campaign = new Campaign();
  private stepNumber: number = 5;
  private stepNavigation: Array<number> = [];
  public step: number = 1;
  private priorities: object[];
  private savedPriorities: object[] = [];
  private targetId: number;

  private savedCategories: any[] = [];
  private savedProductCouponsF: any[] = [];
  private savedProductCouponsNF: any[] = [];

  private returnedSavedCategories: any[] = [];
  private returnedSavedProductCouponsF: any[] = [];
  private returnedSavedProductCouponsNF: any[] = [];
  public missingNameErrors: any[] = [];
  public budgetWarnings: any[] = [];
  private minBudget: number;
  private nextDisabled: boolean = true;
  private availableTargeting: boolean;
  private targetedStores: string;
  private discounts: any[];

  translations: any;

  constructor(
    private campaignService: CampaignService,
    private route: ActivatedRoute,
    private router: Router,
    private srvNotify: MessageService,
    private confirmationService: ConfirmationService,
    private translate: TranslateService
  ) { }

  ngOnInit()
  {
    this.translate.get([
      'campaign.goBackConfirmHeader',
      'campaign.goBackConfirmMsg',
      'campaign.goBack',
      'campaign.cancel'
    ]).subscribe( res => {
      this.translations = res;
    });

    const campaignName = this.route.snapshot.queryParams['campaign'];
    if(typeof campaignName !== 'undefined') {
      this.campaignService.getCampaign(campaignName).subscribe(campaignData => {
        this.campaign = new Campaign(campaignData);
        let stepsToSkip = campaignData.campaignState === 'IN_RIEPILOGO' ? [1, 2, 3, 4] : [1, 2, 3];
        this.stepNavigation.push(...stepsToSkip);
        this.step = campaignData.campaignState === 'IN_RIEPILOGO' ? 5 : 4;
        this.router.navigate(['/new-campaign', this.step]);
        this.targetedStores = campaignData.targetedStores;
      });
    }

    this.urlParams = this.route.params.subscribe(params => {
      this.step = parseInt(params['step']);
      this.stepNavigation.push(this.step);
      // check all prev steps have been completed
      for(let step = this.step; step >= 1; step--) {
        if(this.stepNavigation.indexOf(step) === -1) {
          this.router.navigate(['/new-campaign', '1']);
          return;
        }
      }
    });
  }

  ngDoCheck()
  {
    const isCompleted = this.isStepCompleted();
    this.nextDisabled = !isCompleted;
    this.campaignHeader.saveDisabled = !isCompleted;
  }

  ngOnDestroy()
  {
    this.urlParams.unsubscribe();
  }

  currentStep()
  {
    const steps: CampaignStep[] = [
      this.step1,
      this.step2,
      this.step3,
      this.step4,
      this.step5
    ];

    return steps[this.step - 1];
  }

  isStepCompleted()
  {
    const step = this.currentStep();

    if(typeof step === 'undefined') {
      return false;
    }

    return step.isStepCompleted();
  }

  saveAndExit(event: any)
  {
    if(this.step === 3) {
      if(!this.isStepCompleted()) {
        return;
      }

      this.saveCampaign().then(res => {
        this.campaignNameExists(false);
        this.goToList(true);
      }, err => {
        this.campaignNameExists(true);
      });

      return;

    } else if (this.step === 4) {
      this.step4.saveCampaignAndExit()
      .then(() => this.goToList(true))
      .catch((err) => console.error(err))
    } else {
      this.goToList(false);
    }

  }

  private goToList(saved: boolean)
  {
    this.router.navigate(['/campaign-list'], { queryParams: { saved: +saved } });
  }

  nextStep()
  {
    new Promise((resolve, reject) => {
      // next actions
      switch(this.step)
      {
        case 1:
          if (this.campaign.targetId == this.targetId) {
            this.savedPriorities = this.priorities;
          } else {
            this.savedPriorities = [];
          }
          resolve();
          break;

        case 3:
            this.checkCampaignErrors();
            if (this.missingNameErrors.length > 0 || this.budgetWarnings.length > 0) {
              return;
            } else {
              this.saveCampaign().then(res => {
                this.campaignNameExists(false);
                this.srvNotify.add({severity: 'success', summary: 'Success', detail: 'Campaign created successfully.'});
                resolve(res);
              }, err => {
                this.campaignNameExists(true);
                reject(err);
              });
            }
            break;

        case 4:
          this.campaignService.prepareIstanzaWork(this.campaign.name).subscribe(
            () => this.step4.generateCoupons(resolve)
          )
          break;

        case 5:
          this.campaignService.confirmCampaign(this.campaign.name).subscribe(res => {
            this.router.navigate(['/campaign-list'], { queryParams: { saved: 1 } });
            resolve(res);
          });
          return;

        default:
          resolve();
          break;
      }
    }).then(res => {
      // end wizard
      if(this.step == this.stepNumber) {
        return;
      }

      // next step
      if(this.step < this.stepNumber) {
        this.step++;
      }

      // navigation
      if(this.stepNavigation.indexOf(this.step) === -1) {
        this.stepNavigation.push(this.step);
      }
      this.router.navigate(['/new-campaign', this.step]);
    }, err => {

    });
  }

  prevStep()
  {
    if (this.step == 2) {
      this.targetId = this.campaign.targetId;
    } else if (this.step == 3) {
      this.savedPriorities = this.priorities;
    } else if (this.step == 5) {
      this.confirmationService.confirm({
        header: this.translations['campaign.goBackConfirmHeader'],
        message: this.translations['campaign.goBackConfirmMsg'],
        accept: () => this.campaignService.setCampaignCreated(this.campaign.name).subscribe(
          res => res,
          err => console.error(err),
          () => {
            if (this.campaign.coupons && this.campaign.coupons.length > 0) {
              for(let coupon of this.campaign.coupons) {
                coupon.couponList = [];
              }
            }
            this.step--;
          }),
        acceptLabel: this.translations['campaign.goBack'],
        rejectLabel: this.translations['campaign.cancel']
      });
    }

    if(this.step > 1 && this.step !== 5) {
      this.step--;
    }

    this.router.navigate(['/new-campaign', this.step]);
  }

  saveCampaign()
  {
    const campaignData = {
      campaignName: this.campaign.name,
      emissionStartDate: moment(this.campaign.emissionStartDate).format('DD/MM/YYYY'),
      emissionEndDate: moment(this.campaign.emissionEndDate).format('DD/MM/YYYY'),
      redemptionStartDate: moment(this.campaign.redemptionStartDate).format('DD/MM/YYYY'),
      redemptionEndDate: moment(this.campaign.redemptionEndDate).format('DD/MM/YYYY'),
      budget: this.campaign.budget,
      notes: this.campaign.notes,
      businessGoalCode: this.campaign.targetId,
      flagOnlyCardHolder: this.campaign.flagOnlyCardHolder ? '1' : '0',
      flagOnlyLoyaltyCard: this.campaign.flagOnlyLoyaltyCard ? '1' : '0',
      flagAllChannels: this.campaign.flagAllSelectedChannels ? '1' : '0',
      channelCodeList: this.campaign.channelCodeList,
      budgetInModel: {
        prcPercentageDiscount: this.discounts[1].value,
        prcPercentageId: 2,
        prcMonetoryDiscount: this.discounts[0].value,
        prcMonetoryId: 1,
        prcLoyaltyPoints: this.discounts[2].value,
        prcLoyaltyId: 3
      }
    };

    return this.campaignService.campaignCreation(campaignData).toPromise();
  }

  campaignNameError(error: boolean)
  {
    this.campaignHeader.campaignNameError = error;
  }

  budgetChangeHandler(minBudget) {
    this.minBudget = minBudget;
  }

  campaignNameExists(invalid: boolean)
  {
    this.campaignHeader.campaignNameExists = invalid;
  }

  prioritiesHandler(priorities: object[]) {
    this.priorities = priorities;
  }

  saveSelectedCategories(event) {
    this.savedCategories = event;
  }

  saveSelectedProductCouponsF(event) {
    this.savedProductCouponsF = event;
  }

  saveSelectedProductCouponsNF(event) {
    this.savedProductCouponsNF = event;
  }

  returnSavedCategories(event) {
    this.returnedSavedCategories = event;
  }

  returnSavedProductCouponsF(event) {
    this.returnedSavedProductCouponsF = event;
  }

  returnSavedProductCouponsNF(event) {
    this.returnedSavedProductCouponsNF = event;
  }

  checkCampaignErrors()
  {
    this.missingNameErrors = [];
    this.budgetWarnings = [];
    if(typeof this.campaign.name === 'undefined' || this.campaign.name == '') {
      this.missingNameErrors.push({
        severity: 'error',
        summary: 'Error',
        detail: 'Nome campagna vuoto'
      });
    }

    if(!this.campaign.name.replace(/\s/, '').match(/^[a-zA-Z0-9_-]+$/)) {
      this.missingNameErrors.push({
        severity: 'error',
        summary: 'Error',
        detail: 'Il nome della Campagna può contenere solo caratteri 0-9 A-Z _ -'
      });
    }

    if(this.campaign.budget < this.minBudget) {
      this.budgetWarnings.push({
        severity: 'warn',
        summary: 'Error',
        detail: 'Budget deve essere almeno '+ this.minBudget
      });
    }

  }

  availableTargetingHandler(event) {
    this.availableTargeting = event;
  }

  startTargeting() {
    this.campaignService.startTargeting(this.campaign.name).subscribe(res => {
      if (res.id) {
        this.saveAndExit(null);
      }
    });
  }

  discountsChangedHandler(event) {
    this.discounts = event;
  }

}



// WEBPACK FOOTER //
// ./src/app/views/campaign/new-campaign/new-campaign.component.ts